import { ProvidersResponse } from '../../types';
import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { analytics } from '../../utils/analytics';
import { get } from '../apiService';

interface ProviderFilterParams {
  [key: string]: string | string[] | undefined;
}

type ProvidersListResponse = {
  data: ProvidersResponse | null;
  loading: boolean;
};

const providersListInFlight = new Map<string, Promise<ProvidersListResponse>>();

const buildProvidersListRequestKey = (
  startToken?: string | null,
  filters?: ProviderFilterParams,
  limit?: number,
): string => {
  const normalizedFilters: Record<string, string | string[]> = {};

  if (filters) {
    Object.keys(filters)
      .sort()
      .forEach((key) => {
        const value = filters[key];
        if (value === undefined || value === '') return;

        normalizedFilters[key] = Array.isArray(value)
          ? [...value].filter(Boolean).sort()
          : value;
      });
  }

  return JSON.stringify({
    startToken: startToken ?? null,
    limit: limit ?? null,
    filters: normalizedFilters,
  });
};

export const getProvidersList = async (
  startToken?: string | null,
  filters?: ProviderFilterParams,
  limit?: number
): Promise<ProvidersListResponse> => {
  const requestKey = buildProvidersListRequestKey(
    startToken,
    filters,
    limit,
  );
  const existing = providersListInFlight.get(requestKey);
  if (existing) {
    return existing;
  }

  const request = (async (): Promise<ProvidersListResponse> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer Id is not available');
  }

  const queryParams: Record<string, string> = {
    customerId: customer,
  };

  if (startToken) queryParams.startToken = startToken;
  if (limit) queryParams.limit = limit.toString();

  // Params the backend expects as repeated query params when arrays
  // (e.g. ?providerIds=a&providerIds=b), rather than a single value.
  const multiValueKeys = ['marketId', 'states', 'providerIds'];

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        // Multi-value keys are appended separately below instead.
        if (multiValueKeys.includes(key) && Array.isArray(value)) {
          return;
        }

        // For other filters or single values, use single value (ensure it's a string)
        queryParams[key] = Array.isArray(value) ? value[0] : value;
      }
    });
  }

  // Build URL with query parameters
  const searchParams = new URLSearchParams(queryParams);

  // Append each multi-value key as a repeated query param (array values
  // are skipped above so they land here instead of being truncated to
  // their first element).
  multiValueKeys.forEach((key) => {
    const value = filters?.[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) searchParams.append(key, item);
      });
    }
  });

  const url = `/resourceMatrix/providers/list?${searchParams.toString()}`;

  let loading = true;
  try {
    const responseData = await get(url);
    const data = responseData as ProvidersResponse;
    loading = false;
    analytics.trackEvent('list', 'provider');
    return { data, loading };
  } catch (error) {
    loading = false;
    console.error('Failed to fetch providers list:', error);
    throw new Error('Failed to fetch providers list');
  }
  })();

  providersListInFlight.set(requestKey, request);

  try {
    return await request;
  } finally {
    const current = providersListInFlight.get(requestKey);
    if (current === request) {
      providersListInFlight.delete(requestKey);
    }
  }
};

